import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CommonIcons } from 'common-src/assets/Icons';
import { DatePickerNew, RequestHandlerScreen, Select } from 'common-src/components/base';
import { deleteOrmItems } from 'common-src/features/rest';
import { restRequestMultiple } from 'common-src/features/rest/actions';
import {
  getActiveMfaExemptions,
  getExemptionsRequest,
} from 'common-src/models/PatientBillingExemption';
import {
  ACTIVE_STATUSES,
  getPatientCoverages,
  getPatientCoveragesRequest,
  getPlanCategory,
} from 'common-src/models/PatientCoverage';
import PatientCoverageHierarchy, {
  createPatientCoverageHierarchy,
  getCoverageHierarchyRequest,
  getPatientCoverageHierarchies,
  updatePatientCoverageHierarchy,
} from 'common-src/models/PatientCoverageHierarchy';
import { getClosestDate, getDateWithTimeZone } from 'common-src/utils/dateUtils';

import { ModalType } from 'src/components/base/ModalGroup';
import { BaseTable, TablePagination, TextItem } from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useFormButtons from 'src/hooks/useFormButtons';
import useInterval from 'src/hooks/useInterval';
import useRequestLoading from 'src/hooks/useRequestLoading';

import { Operation, TableHeaders } from './constants';
import {
  findPriorityByDate,
  getCreatePayload,
  getRequestOperations,
  validateCoverageHierarchy,
} from './helpers';
import styles from './PayorPriority.module.scss';

const newDateOption = Object.freeze({
  value: 'New',
  label: 'New',
  icon: CommonIcons.calendarNewIcon,
});

const PayorPriority = ({ patientId, actionsAllowed }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [forceOpenDatePicker, setForceOpenDatePicker] = useState(false);
  const [activeCoveragesCount, setActiveCoveragesCount] = useState(0);

  const patientCoverages = useCustomSelector((state) => getPatientCoverages(state, patientId));
  const coveragePriorities = useCustomSelector((state) =>
    getPatientCoverageHierarchies(state, patientId),
  );
  const mfaExemptions = useCustomSelector((state) => getActiveMfaExemptions(state, patientId));

  const isNewEffectiveDateSelected = useMemo(() => {
    const allEffectiveDates = coveragePriorities.map((cp) =>
      getDateWithTimeZone(cp.effectiveDate, 'MM/DD/YYYY'),
    );

    return !allEffectiveDates.includes(effectiveDate);
  }, [effectiveDate, coveragePriorities]);

  const { showAlert, AlertType } = useAlert();

  const { isRequesting } = useRequestLoading([
    getPatientCoveragesRequest({ patientId }),
    getCoverageHierarchyRequest({ patientId }),
    getExemptionsRequest({ patientId, exemption_types: 1 }),
  ]);

  useInterval(() => {
    dispatch(getPatientCoveragesRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
    dispatch(getCoverageHierarchyRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
    dispatch(getExemptionsRequest({ patientId, exemption_types: 1 }, {}));
  });

  const getSubmitCallback = (isEdit, setIsLoading, setIsEditMode, deleteOrmModels = false) => ({
    successBlock: () => {
      if (deleteOrmModels) {
        deleteOrmItems(PatientCoverageHierarchy.modelName);
        dispatch(getCoverageHierarchyRequest({ patientId }));
      }

      setIsLoading(false);
      setIsEditMode(false);
      showAlert(
        AlertType.Success,
        '',
        `You have successfully ${isEdit ? 'updated' : 'created'} payor priority.`,
      );
    },
    errorBlock: (err) => {
      setIsLoading(false);
      showAlert(AlertType.Error, '', err);
    },
  });

  const onSubmitHandler = (setIsLoading, setIsEditMode) => {
    const err = validateCoverageHierarchy(data);

    if (err) {
      showAlert(AlertType.Error, '', err);
      return;
    }

    setIsLoading(true);
    if (isNewEffectiveDateSelected) {
      dispatch(
        createPatientCoverageHierarchy(
          _.compact(getCreatePayload(data, effectiveDate, patientId)),
          getSubmitCallback(false, setIsLoading, setIsEditMode, false),
        ),
      );
    } else {
      const operations = getRequestOperations(data, effectiveDate, coveragePriorities, patientId);
      const requests = [];
      let deleteOrmModels = false;
      if (!_.isEmpty(_.compact(operations[Operation.Create]))) {
        requests.push(createPatientCoverageHierarchy(_.compact(operations[Operation.Create])));
      }

      if (!_.isEmpty(operations[Operation.Update])) {
        // should refresh ORM models when deleting some hierarchy
        deleteOrmModels = _.some(operations[Operation.Update], { effectiveDate: null });
        requests.push(updatePatientCoverageHierarchy(operations[Operation.Update]));
      }

      dispatch(
        restRequestMultiple({
          restRequests: requests,
          ...getSubmitCallback(true, setIsLoading, setIsEditMode, deleteOrmModels),
        }),
      );
    }
  };

  const { isEditMode, renderContent, setInitialData, setIsLoading, setIsEditMode } = useFormButtons(
    'Hierarchy',
    setData,
    () => onSubmitHandler(setIsLoading, setIsEditMode),
    data,
    false,
    null,
    !!effectiveDate && activeCoveragesCount > 0,
    isNewEffectiveDateSelected,
  );

  const activeCoverages = useMemo(() => {
    const groupedCoveragePriorities = _.groupBy(coveragePriorities, 'ref.patientCoverageId');
    const groupedCoveragesByDate = _.mapValues(groupedCoveragePriorities, (group) => {
      const sortedGroup = _.orderBy(group, ['effectiveDate'], ['desc']);

      return _.filter(sortedGroup, (item) =>
        !isEditMode
          ? moment(effectiveDate).isSame(moment(item.effectiveDate))
          : moment(effectiveDate).startOf('day').isSameOrAfter(moment(item.effectiveDate)) &&
            (!item.patientCoverage?.endDate ||
              moment(effectiveDate)
                .startOf('day')
                .isSameOrBefore(moment(item.patientCoverage?.endDate).startOf('day'))),
      );
    });

    const coveragePrioritiesByDate = _.compact(
      _.map(groupedCoveragesByDate, (group) => (group.length > 0 ? group[0] : null)),
    );

    const coveragesToFilter =
      effectiveDate && !isEditMode ? coveragePrioritiesByDate : coveragePriorities;

    const allCoverageIds = coveragesToFilter.map((cp) => cp.patientCoverage?.id);

    const uniqueCoverageIds = _.uniq(allCoverageIds);

    // 1. All active coverage plans
    // 2. Selected effective date should be after plan start date and before plan end date
    // 3. Selected effective date should be after priority effective date (in non-edit mode)
    return patientCoverages.filter(
      (c) =>
        (ACTIVE_STATUSES.includes(c.status) ||
          moment(effectiveDate).startOf('day').isBefore(moment(c.endDate).endOf('day'))) &&
        (!effectiveDate ||
          (moment(effectiveDate).endOf('day').isSameOrAfter(moment(c.startDate)) &&
            (!c?.endDate ||
              moment(effectiveDate).startOf('day').isBefore(moment(c.endDate).endOf('day'))))) &&
        (isEditMode || uniqueCoverageIds.includes(c.id)),
    );
  }, [patientCoverages, coveragePriorities, effectiveDate, isEditMode]);

  const hierarchyOptions = useMemo(() => {
    const hierarchiesArray = Array.from(
      { length: Math.max(activeCoverages.length + 1, 2) },
      (_, i) => i,
    ).map((item) => ({
      label: item,
      value: item,
    }));

    // no hierarchy option
    hierarchiesArray.push({ label: '-', value: null });

    return hierarchiesArray;
  }, [activeCoverages.length]);

  const effectiveDatesOptions = useMemo(() => {
    const allEffectiveDates = coveragePriorities.map((cp) =>
      getDateWithTimeZone(cp.effectiveDate, 'MM/DD/YYYY'),
    );

    // effective date is newly selected from date picker
    if (effectiveDate && isNewEffectiveDateSelected) {
      // push the date as valid option in edit or remove it in non-edit mode
      if (isEditMode) {
        allEffectiveDates.push(effectiveDate);
      } else {
        setEffectiveDate(null);
      }
    }

    const sortedDates = allEffectiveDates.sort((a, b) => new Date(b) - new Date(a));

    const uniqueDatesOptions = _.uniq(sortedDates).map((date) => ({
      value: date,
      label: date,
    }));

    if (isEditMode) {
      uniqueDatesOptions.push(newDateOption);
    }

    return uniqueDatesOptions;
  }, [coveragePriorities, patientCoverages, isEditMode, effectiveDate]);

  useEffect(() => {
    if (effectiveDate || _.isEmpty(coveragePriorities)) return;

    const allEffectiveDates = coveragePriorities.map((cp) =>
      getDateWithTimeZone(cp.effectiveDate, 'MM/DD/YYYY'),
    );

    setEffectiveDate(getClosestDate(_.uniq(allEffectiveDates)));
  }, [effectiveDate, coveragePriorities]);

  useEffect(() => {
    const data = activeCoverages.map((c) => {
      const priority = findPriorityByDate(coveragePriorities, c, effectiveDate);

      return {
        id: priority?.id ?? Math.random(),
        hierarchy: priority?.hierarchy ?? null,
        coverageId: c.id,
      };
    });

    setInitialData(data);
    setData(data);
  }, [activeCoverages, coveragePriorities, effectiveDate]);

  useEffect(() => {
    setActiveCoveragesCount(activeCoverages?.length || 0);
  }, [activeCoverages?.length]);

  const onChangeHierarchy = (option, item) => {
    const dataCopy = JSON.parse(JSON.stringify(data));
    const objIndex = dataCopy.findIndex((obj) => obj.id === item.id);

    if (objIndex === -1) return;

    dataCopy[objIndex].hierarchy = option.value;
    setData(dataCopy);
  };

  const openDatePicker = () => {
    setForceOpenDatePicker(true);
    setTimeout(() => {
      setForceOpenDatePicker(false);
    }, 0);
  };

  const onChangeEffectiveDate = (option) => {
    // clear option
    if (!option) {
      setEffectiveDate(null);
      return;
    }

    // new date option case
    if (option.value === newDateOption.value) {
      openDatePicker();
      return;
    }

    setEffectiveDate(option.value);
  };

  const renderHierarchy = (coverageId) => {
    const priority = data.find((item) => item.coverageId === coverageId);

    if (isEditMode) {
      return (
        <Select
          id={`hierarchy-${priority?.id}`}
          options={hierarchyOptions}
          onChange={(option) => onChangeHierarchy(option, priority)}
          value={hierarchyOptions.find((op) => op.value === (priority?.hierarchy ?? null)) || null}
          withError={false}
          text={Number.isInteger(priority?.hierarchy) ? priority.hierarchy : '-'}
          placeholder="-"
          size="small"
        />
      );
    }

    return (
      <TextItem
        isActive
        text={Number.isInteger(priority?.hierarchy) ? priority.hierarchy : '-'}
        disableTooltip
      />
    );
  };

  const getData = () =>
    activeCoverages.map((coverage) => ({
      payorName: (
        <TextItem
          isActive
          isUnderlined={ACTIVE_STATUSES.includes(coverage.status)}
          disableTooltip
          text={coverage.payorName}
          classNames={[styles.tableCell]}
          onClick={() => {
            if (!ACTIVE_STATUSES.includes(coverage.status)) return;
            dispatch(openModal(ModalType.EDIT_PLAN, { planId: coverage.id, patientId }));
          }}
        />
      ),
      planName: <TextItem isActive text={coverage.planName || '-'} disableTooltip />,
      payorType: <TextItem isActive text={getPlanCategory(coverage.payorType)} disableTooltip />,
      startDate: (
        <TextItem
          isActive
          text={coverage.startDate ? moment(coverage.startDate).format('MM/DD/YYYY') : '-'}
          disableTooltip
        />
      ),
      endDate: (
        <TextItem
          isActive
          text={coverage.endDate ? moment(coverage.endDate).format('MM/DD/YYYY') : '-'}
          disableTooltip
        />
      ),
      hierarchy: renderHierarchy(coverage.id),
    }));

  const visibleMfaExemptions = mfaExemptions.filter(
    (exemption) =>
      !exemption.endDate ||
      moment(exemption.endDate).startOf('day').isSameOrAfter(moment().startOf('day')),
  );

  const maxCoverageHierarchy = data.reduce(
    (max, item) => (Number.isInteger(item.hierarchy) ? Math.max(max, item.hierarchy) : max),
    0,
  );

  const getMfaRows = () =>
    visibleMfaExemptions.map((exemption, index) => ({
      payorName: (
        <TextItem
          isActive
          disableTooltip
          text="MFA – Medical Financial Assistance"
          classNames={[styles.tableCell]}
        />
      ),
      planName: <TextItem isActive disableTooltip text={`${exemption.exemptionRate}% Waiver`} />,
      payorType: <TextItem isActive disableTooltip text="Exemption" />,
      startDate: (
        <TextItem
          isActive
          disableTooltip
          text={exemption.startDate ? moment(exemption.startDate).format('MM/DD/YYYY') : '-'}
        />
      ),
      endDate: (
        <TextItem
          isActive
          disableTooltip
          text={exemption.endDate ? moment(exemption.endDate).format('MM/DD/YYYY') : '-'}
        />
      ),
      hierarchy: (
        <TextItem isActive disableTooltip text={String(maxCoverageHierarchy + 1 + index)} />
      ),
    }));

  if (isRequesting) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling priorities..." />;
  }

  return (
    <>
      <div className={styles.subheader}>
        <div className={styles.dateContainer}>
          <span className={styles.text}>Priority effective date</span>
          <Select
            id="priority-effective-date-filter"
            classNames={[styles.select]}
            options={effectiveDatesOptions}
            onChange={onChangeEffectiveDate}
            value={effectiveDatesOptions.find((op) => op?.value === effectiveDate) || null}
            withError={false}
            placeholder="MM/DD/YYYY"
            size="small"
          />
          <DatePickerNew
            id="new-effective-date-picker"
            classNames={[styles.pickerContainer]}
            inputClassNames={[styles.pickerInput]}
            forceOpen={forceOpenDatePicker}
            onDateSelected={(date) => {
              const selectedDate = date ? moment(date).format('MM/DD/YYYY') : null;
              setEffectiveDate(selectedDate);
            }}
            filterDate={(date) => {
              const day = date.getDate();
              return day === 1; // Allow only the 1st of each month
            }}
          />
        </div>
        {renderContent(true, !actionsAllowed)}
      </div>
      <BaseTable
        headers={TableHeaders}
        data={[...getData(), ...getMfaRows()]}
        name="payor-priority"
        classNames={[
          'medical-table-new',
          (activeCoverages?.length || 0) + visibleMfaExemptions.length <= ResultsCount[25]
            ? 'no-pagination'
            : '',
        ]}
        emptyText="No payor priorities"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'hierarchy',
          label: 'Hierarchy',
          width: '15%',
          sortBy: 'text',
        }}
        paginationData={paginationData}
      />
      <TablePagination
        onChangePageHandler={setPaginationData}
        results={[...activeCoverages, ...visibleMfaExemptions]}
      />
    </>
  );
};

PayorPriority.propTypes = {
  patientId: PropTypes.number,
  actionsAllowed: PropTypes.bool,
};

export default PayorPriority;
